import { useCallback, useState } from 'react';
import {
  setTovQueryData,
  useTovQuery,
  useTovQueryClient,
} from '@tov-plus/frontend-data';

import { guestApi } from '../app/api';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import {
  BrowserHttpError,
  isNetworkUnavailableError,
} from '../lib/http/createBrowserHttpClient';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import type { GuestGalleryData } from './galleryHelpers';
import { msg, useToast } from '@tov-plus/ui';

export type GalleryStatus = 'loading' | 'ready' | 'error';
export type GalleryErrorKind =
  'no_access' | 'not_available' | 'network' | 'unexpected';

interface UseGuestGalleryResult {
  status: GalleryStatus;
  gallery: GuestGalleryData | null;
  reload: () => Promise<void>;
  loadMore: () => Promise<void>;
  updateGallery: (
    updater: (current: GuestGalleryData) => GuestGalleryData,
  ) => void;
  isLoadingMore: boolean;
  errorKind: GalleryErrorKind | null;
}

function resolveGalleryErrorKind(error: unknown): GalleryErrorKind {
  if (error instanceof BrowserHttpError) {
    if (error.statusCode === 401 || error.statusCode === 403) {
      return 'no_access';
    }

    if (error.statusCode === 404) {
      return 'not_available';
    }
  }

  if (isNetworkUnavailableError(error)) {
    return 'network';
  }

  return 'unexpected';
}

// Owns loading the guest gallery payload and its loading/error state, so the
// page component stays focused on presentation.
export function useGuestGallery(
  filters: {
    albumId?: string | null;
    eventId?: string | null;
    search?: string;
    favoritesOnly?: boolean;
    myUploads?: boolean;
  } = {},
): UseGuestGalleryResult {
  const access = useGuestAccess();
  const toast = useToast();
  const queryClient = useTovQueryClient();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const baseQueryKey =
    access.status === 'ready'
      ? guestQueryKeys.gallery(access.occasionSlug, access.guestToken)
      : guestQueryKeys.gallery('pending', 'pending');
  const galleryQueryKey = [
    ...baseQueryKey,
    filters.albumId ?? '',
    filters.eventId ?? '',
    filters.search ?? '',
    Boolean(filters.favoritesOnly),
    Boolean(filters.myUploads),
  ] as const;
  const galleryQuery = useTovQuery({
    queryKey: galleryQueryKey,
    queryFn: () => {
      if (access.status !== 'ready') {
        throw new Error('Guest access is not ready.');
      }

      return guestApi.getGallery(
        access.occasionSlug,
        access.guestToken,
        filters,
      );
    },
    enabled: access.status === 'ready',
    placeholderData: (previousData, previousQuery) =>
      JSON.stringify(previousQuery?.queryKey.slice(0, baseQueryKey.length)) ===
      JSON.stringify(baseQueryKey)
        ? previousData
        : undefined,
  });
  const gallery = galleryQuery.data ?? null;

  const fetchGallery = useCallback(
    async (cursor?: string | null) => {
      if (access.status !== 'ready') {
        return null;
      }

      return guestApi.getGallery(access.occasionSlug, access.guestToken, {
        ...filters,
        ...(filters.myUploads ? { myUploadsCursor: cursor } : { cursor }),
      });
    },
    [
      access,
      filters.albumId,
      filters.eventId,
      filters.search,
      filters.favoritesOnly,
      filters.myUploads,
    ],
  );

  const reload = useCallback(async () => {
    await galleryQuery.refetch();
  }, [galleryQuery]);

  const loadMore = useCallback(async () => {
    const cursor = filters.myUploads
      ? gallery?.myUploadsPageInfo.nextCursor
      : gallery?.pageInfo.nextCursor;
    if (!cursor || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const response = await fetchGallery(cursor);

      if (!response) {
        return;
      }

      setTovQueryData<GuestGalleryData>(
        queryClient,
        galleryQueryKey,
        (current) => {
          if (!current) {
            return response;
          }

          const existingIds = new Set(
            current.items.map((item) => item.galleryItem.id),
          );
          const nextItems = response.items.filter(
            (item) => !existingIds.has(item.galleryItem.id),
          );
          const ownIds = new Set(
            current.myUploads.map((item) => item.galleryItem.id),
          );

          return {
            ...current,
            items: [...current.items, ...nextItems],
            myUploads: filters.myUploads
              ? [
                  ...current.myUploads,
                  ...response.myUploads.filter(
                    (item) => !ownIds.has(item.galleryItem.id),
                  ),
                ]
              : current.myUploads,
            myUploadsPageInfo: response.myUploadsPageInfo,
            favoriteItemIds: [
              ...new Set([
                ...current.favoriteItemIds,
                ...response.favoriteItemIds,
              ]),
            ],
            albums: current.albums.map((album) => {
              const ids = new Set(
                album.items.map((entry) => entry.galleryItem.id),
              );
              const incoming = response.albums.find(
                (entry) => entry.album.id === album.album.id,
              );
              return {
                ...album,
                items: [
                  ...album.items,
                  ...(incoming?.items ?? []).filter(
                    (entry) => !ids.has(entry.galleryItem.id),
                  ),
                ],
              };
            }),
            pageInfo: response.pageInfo,
          };
        },
      );
    } catch {
      toast.error(msg('pages.gallery_page.check_connection_and_try_again'));
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    fetchGallery,
    gallery,
    galleryQueryKey,
    isLoadingMore,
    queryClient,
    filters.myUploads,
    toast,
  ]);

  const updateGallery = useCallback(
    (updater: (current: GuestGalleryData) => GuestGalleryData) => {
      setTovQueryData<GuestGalleryData>(
        queryClient,
        galleryQueryKey,
        (current) => (current ? updater(current) : current),
      );
    },
    [galleryQueryKey, queryClient],
  );

  return {
    status: galleryQuery.isLoading
      ? 'loading'
      : galleryQuery.isError
        ? 'error'
        : 'ready',
    gallery,
    reload,
    loadMore,
    updateGallery,
    isLoadingMore,
    errorKind: galleryQuery.isError
      ? resolveGalleryErrorKind(galleryQuery.error)
      : null,
  };
}
